import axios, { AxiosError } from "axios";
import { useQuery, UseQueryResult } from "react-query";
import { ShiftLocationsResponse } from "../model/ShiftLocation";
import { ShiftLocationsRequest } from "../request/ShiftLocationsRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

const ShiftLocationsApiService = async (
  request: ShiftLocationsRequest
): Promise<ShiftLocationsResponse> => {
  try {
    const { data } = await axios.get("/shift-locations/overview/", {
      params: {
        // Use explicit undefined check to preserve employer: 0
        ...(request.employer !== undefined && { employer: request.employer }),
        ...(request.date_from && { date_from: request.date_from }),
        ...(request.date_to && { date_to: request.date_to }),
        ...(request.client && { client: request.client }),
        ...(request.has_exceptions !== undefined && {
          has_exceptions: request.has_exceptions,
        }),
        ...(request.summary && { summary: true }),
      },
    });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);

    customError.cause = axiosError;
    throw customError;
  }
};

const useShiftLocationsApiService = (
  request: ShiftLocationsRequest
): UseQueryResult<ShiftLocationsResponse, Error | AxiosError> => {
  const { enabled = true, ...queryParams } = request;
  // Normalize undefined values to null for consistent cache key. ``summary``
  // is part of the key so the dropdown's slim payload never overwrites the
  // overview/exceptions full payload in the React Query cache.
  const normalizedParams = {
    employer: queryParams.employer ?? null,
    date_from: queryParams.date_from ?? null,
    date_to: queryParams.date_to ?? null,
    client: queryParams.client ?? null,
    has_exceptions: queryParams.has_exceptions ?? null,
    summary: queryParams.summary ?? null,
  };
  return useQuery<ShiftLocationsResponse, Error | AxiosError>(
    ["ShiftLocationsApiService", ...Object.values(normalizedParams)],
    () => ShiftLocationsApiService(queryParams),
    {
      keepPreviousData: true,
      enabled,
      // The slim summary payload is static metadata (location list +
      // working shift configs) — cache aggressively so the picker
      // doesn't refetch on every reports-page mount.
      ...(queryParams.summary ? { staleTime: 10 * 60_000 } : {}),
    }
  );
};

export { ShiftLocationsApiService, useShiftLocationsApiService };
